import _ from 'lodash'
import React from 'react'
import cityPopulations, { stateCodesList } from './cityPopulations'
import { forEachPromise } from '../../utils'
import api from '../../api'
import Select from '../Select'
import Table from '../Table'
import toast from '../Toast'
import getCitySummaries from './getCitySummaries'
import CitySummary from './CitySummary'
import queryFormatOptions, { formatQuery } from './queryFormatOptions'

let cityListSize = 10

let Analyze = ({
  queryFormatState,
  selectedCityIndexesState,
  savedHistoryIdsState,
  state: {
    cityFilter,
    stateCodes,
    populationMin,
    populationMax,
    terms,
    termsArray,
    summary,
    running,
    progressPercent,
  },
  setState,
}) => {
  let [
    selectedCityIndexes,
    toggleCity,
    setSelectedCityIndexes,
  ] = selectedCityIndexesState
  let [
    savedHistoryIds,
    toggleSavedHistoryId,
    setSavedHistoryIds,
  ] = savedHistoryIdsState
  let [queryFormats, toggeQueryFormat] = queryFormatState
  let popMin = parseFloat(populationMin)
  let popMax = parseFloat(populationMax)

  let filteredCityPopulations = cityPopulations.filter(
    ({ city, stateCode, population }) => {
      return _.every([
        !cityFilter || _.toLower(city).includes(_.toLower(cityFilter)),
        _.isEmpty(stateCodes) || stateCodes.includes(stateCode),
        !_.isFinite(popMin) || population >= popMin,
        !_.isFinite(popMax) || population <= popMax,
      ])
    }
  )
  let selectedCities = filteredCityPopulations
    .slice(0, cityListSize)
    .filter((c, index) => selectedCityIndexes.includes(index))

  let analyze = async () => {
    let termsArray = _.chain(terms)
      .split('\n')
      .compact()
      .map(_.trim)
      .uniq()
      .value()
    setSavedHistoryIds([])
    setState({ running: true, progressPercent: 0, summary: [], termsArray })

    let results = []
    let resultTotalCount = termsArray.length * selectedCities.length
    let progressCount = 0
    try {
      await forEachPromise(selectedCities, async ({ city, stateCode }) => {
        let promises = _(queryFormats)
          .map(queryFormatId =>
            _.map(termsArray, async term => {
              let result = await api('/analyze-terms', {
                query: formatQuery(queryFormatId, { term, city, stateCode }),
              })
              results.push({ term, city, stateCode, result, queryFormatId })
              progressCount += 1
              setState({
                progressPercent: (progressCount / resultTotalCount) * 100,
              })
            })
          )
          .flatten()
          .value()
        await Promise.all(promises)
      })

      setState({
        running: false,
        termsArray,
        summary: getCitySummaries({ termsArray, results }),
      })
      toast.success('Analysis complete')
    } catch (e) {
      setState({ running: false })
      toast.error(`there was an error: ${e}`)
    }
  }

  return (
    <div>
      <div className="form-row">
        <div className="form-group col-lg-4">
          <label>Search Terms</label>
          <textarea
            className="form-control"
            placeholder="One term per line"
            rows={5}
            value={terms}
            onChange={e => setState({ terms: e.target.value })}
          />
        </div>
        <div className="form-group col-lg-4">
          <label>Query Formats</label>
          {queryFormatOptions.map(({ label, value }) => {
            return (
              <div className="form-check" key={value}>
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="select-all-checkbox"
                  checked={queryFormats.includes(value)}
                  onChange={() => toggeQueryFormat(value)}
                />
                <label
                  className="form-check-label"
                  htmlFor="select-all-checkbox"
                >
                  {label}
                </label>
              </div>
            )
          })}
        </div>
      </div>
      <div className="form-row">
        <div className="form-group col-lg-4">
          <label>City</label>
          <input
            className="form-control"
            value={cityFilter}
            onChange={e => setState({ cityFilter: e.target.value })}
          />
        </div>
        <div className="form-group col-lg-4">
          <label>State</label>
          <Select
            isMulti
            onChange={change => setState({ stateCodes: change })}
            getOptionLabel={v => v}
            options={stateCodesList}
            value={stateCodes}
          />
        </div>
        <div className="form-group col-lg-2">
          <label>Population Min</label>
          <input
            type="number"
            step={1000}
            className="form-control"
            value={populationMin}
            onChange={e => setState({ populationMin: e.target.value })}
          />
        </div>
        <div className="form-group col-lg-2">
          <label>Population Max</label>
          <input
            type="number"
            step={1000}
            className="form-control"
            value={populationMax}
            onChange={e => setState({ populationMax: e.target.value })}
          />
        </div>
      </div>
      <h5 className="d-flex">
        Showing {filteredCityPopulations.length} of {cityPopulations.length}
        <small
          className="ml-auto align-self-center"
          style={{ fontSize: '12px' }}
        >
          Data from https://simplemaps.com/data/us-cities
        </small>
      </h5>
      <Table
        emptyText="No cities found."
        rowKey={r => `${r.city}-${r.stateCode}`}
        initialSort={{ order: 'desc', index: 2 }}
        columns={[
          {
            header: () => (
              <div className="form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="select-all-checkbox"
                  checked={selectedCityIndexes.length === cityListSize}
                  onChange={() =>
                    setSelectedCityIndexes(
                      selectedCityIndexes.length === cityListSize
                        ? []
                        : _.range(cityListSize)
                    )
                  }
                />
                <label
                  className="form-check-label"
                  htmlFor="select-all-checkbox"
                />
              </div>
            ),
            key: 'select',
            showSort: false,
            style: { width: '80px' },
            render: (r, rowIndex) => (
              <div className="form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  onChange={() => toggleCity(rowIndex)}
                  checked={selectedCityIndexes.includes(rowIndex)}
                />
              </div>
            ),
          },
          { header: 'City', key: 'city' },
          { header: 'State', key: 'stateCode' },
          {
            header: 'Population',
            key: 'population',
            render: ({ population }) => population.toLocaleString('en-US'),
          },
        ]}
        data={filteredCityPopulations.slice(0, cityListSize)}
      />
      <div className="mb-3 d-flex justify-content-center align-items-center">
        <button
          className="btn btn-primary mr-auto"
          onClick={analyze}
          disabled={!terms || !selectedCityIndexes.length || running}
        >
          Analyze {running && <i className="fa fa-spin fa-spinner" />}
        </button>
        {running && (
          <>
            <span className="ml-5 mr-3">Progress:</span>
            <div className="progress col px-0">
              <div
                className="progress-bar"
                role="progressbar"
                style={{ width: `${progressPercent}%` }}
                aria-valuenow={progressPercent}
                aria-valuemin={0}
                aria-valuemax={100}
              />
            </div>
          </>
        )}
      </div>
      <div>
        {summary.map(citySummary => (
          <CitySummary
            key={citySummary.resultId}
            termsArray={termsArray}
            citySummary={citySummary}
            showSaveHistory
            savedHistoryIds={savedHistoryIds}
            toggleSavedHistoryId={toggleSavedHistoryId}
          />
        ))}
      </div>
    </div>
  )
}

export default Analyze
